import { connect, type ConnectedProps } from "react-redux";
import { useCallback, useEffect, useMemo } from "react";

import { bindAction } from "interfaces";
import { bindActionCreators } from "@reduxjs/toolkit";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";

import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type ColumnOption } from "interfaces/columnOptions";

import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { createLicensesAccountPacksFetcher, useLicensesEngagementQuery } from "./Queries/queries";
import { type LicensesAccountPackRow } from "./types";
import { canUseChildAccount, type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { useHasCreateAccountsAddOn } from "features/Reporting/Content/queries/useHasCreateAccountsAddOn";
import { doLicensesAccountPacksExport } from "./Queries/doLicensesAccountPacksExport";

import { Title } from "components/listViewTemplates";
import dateTimeUtils from "utils/dateTimeUtils";
import utils from "utils/miscellaneousUtils";
import styles from "./packsReport.module.scss";

type Props = PropsFromRedux & {
  filter: PerformanceWithAccountFilter;
};

const PacksReportPackCustomersDrilldown = ({
  filter,
  setHasExportData,
  setExportAction,
  licensesAccountPacksExport,
  accountId,
}: Props) => {
  const engagementQuery = useLicensesEngagementQuery(filter);
  const hasCreateAccountsAddOn = useHasCreateAccountsAddOn(accountId);

  useEffect(() => {
    const selectedPackName = filter.packId?.[0]?.text;

    setExportAction({
      method: licensesAccountPacksExport,
      args: [filter, selectedPackName ? `${selectedPackName} Customers Export` : "Pack Customers Export"],
      isExportEnabled: true,
    });
  }, [filter, licensesAccountPacksExport, setExportAction]);

  const fetchPackCustomersPage = useMemo(() => createLicensesAccountPacksFetcher(filter), [filter]);
  const queryKeyBase = useMemo(() => ["LicensesPackCustomers", filter] as const, [filter]);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. This packs report contains no data.",
      }),
    [setHasExportData],
  );

  const columns = useMemo<ColumnOption<LicensesAccountPackRow>[]>(
    () => [
      {
        name: "Customer",
        width: 5,
        isSortable: true,
        sortKey: "CustomerName",
        render: (item) => {
          const childAccountEnabled = canUseChildAccount(item.CustomerId, hasCreateAccountsAddOn);
          return (
            <Title
              title={item.CustomerName}
              url={childAccountEnabled ? "/licensing/packs/available" : undefined}
              target={childAccountEnabled ? "_blank" : undefined}
              moboAccountId={item.CustomerId}
            />
          );
        },
      },
      {
        name: "Licenses Issued",
        width: 3,
        isSortable: true,
        sortKey: "IssuedLicenses",
        render: (item) => item.IssuedLicenses.toLocaleString(),
      },
      {
        name: "Contract Licenses",
        width: 3,
        isSortable: true,
        sortKey: "ContractedLicenses",
        render: (item) => {
          if (item.IsAutoIncrement) {
            return "Auto-Increment";
          }
          if (item.IsUnlimited) {
            return "Unlimited";
          }
          return utils.unlimitedNumber(item.ContractedLicenses);
        },
      },
      {
        name: "Date Enabled",
        width: 3,
        isSortable: true,
        sortKey: "DateEnabled",
        render: (item) => dateTimeUtils.formatEmptyDate(item.DateEnabled),
      },
      {
        name: "Expiration Date",
        width: 3,
        isSortable: true,
        sortKey: "ExpirationDate",
        render: (item) => dateTimeUtils.formatEmptyDate(item.ExpirationDate) ?? "-",
      },
    ],
    [hasCreateAccountsAddOn],
  );

  return (
    <>
      <div className={styles.cards}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && engagementQuery.data && (
            <CardReporting items={engagementQuery.data.customerCards} />
          )}
        </RequestStatusRenderer>
      </div>

      <div className={styles.tableSection}>
        <GenericPerformanceListWithServerPagination
          title="Customers"
          columns={columns}
          filter={filter}
          fetchPage={fetchPackCustomersPage}
          queryKeyBase={queryKeyBase}
          hasDataForExport={updateExportDataState}
          isDateAllTime
        />
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  licensesAccountPacksExport: bindActionCreators(doLicensesAccountPacksExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PacksReportPackCustomersDrilldown);
export default ConnectedComponent;
